/**
 * MarketplacePatterns - Step 1.2 Implementation
 * Comprehensive marketplace pattern management system
 * Connects admin dashboard to 2W billing app's marketplace detection system
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Spinner,
  Banner,
  EmptyState,
  DataTable,
  TextField,
  Select,
  ButtonGroup,
  Tooltip,
  Modal,
  FormLayout,
} from '@shopify/polaris';
import {
  SearchIcon,
  RefreshIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';

import {
  useMarketplacePatterns,
  useCreateMarketplacePattern,
  useUpdateMarketplacePattern,
  useDeleteMarketplacePattern,
} from '../utils/queries';

export default function MarketplacePatterns() {
  const navigate = useNavigate();

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [marketplaceFilter, setMarketplaceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);

  // Modal state
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    marketplaceName: '',
    displayName: '',
    patternType: 'CONTAINS',
    patternValue: '',
    priority: 1,
    isActive: true,
    isCaseSensitive: false,
    category: 'primary',
    description: '',
  });

  // Build query parameters
  const queryParams = useMemo(() => ({
    page: currentPage,
    limit: pageSize,
    ...(searchQuery && { marketplace: searchQuery }),
    ...(statusFilter && { isActive: statusFilter }),
  }), [currentPage, pageSize, searchQuery, statusFilter]);

  // Fetch data
  const {
    data: patternsResponse,
    isLoading,
    error,
    refetch,
    isPreviousData,
  } = useMarketplacePatterns(queryParams);

  // Mutations
  const createPattern = useCreateMarketplacePattern();
  const updatePattern = useUpdateMarketplacePattern();
  const deletePattern = useDeleteMarketplacePattern();

  // Robust API response parser with error handling
  const parseApiResponse = (response) => {
    if (!response) {
      return { patterns: [], pagination: { totalCount: 0, totalPages: 1 } };
    }
    
    try {
      // Handle direct array response
      if (Array.isArray(response)) {
        return {
          patterns: response,
          pagination: { totalCount: response.length, totalPages: 1 }
        };
      }
      
      // Handle object response - try common field names
      if (typeof response === 'object') {
        const patterns = response.patterns || response.data || response.items || [];
        const pagination = response.pagination || response.meta || response.paging || {};
        
        // Ensure patterns is an array
        const safePatterns = Array.isArray(patterns) ? patterns : [];
        
        // Calculate safe pagination values
        const totalCount = pagination.totalCount || pagination.total || pagination.totalItems || safePatterns.length || 0;
        const totalPages = pagination.totalPages || pagination.pages || Math.ceil(totalCount / pageSize) || 1;
        
        return {
          patterns: safePatterns,
          pagination: { totalCount, totalPages }
        };
      }
      
      // Fallback for unexpected response format
      return { patterns: [], pagination: { totalCount: 0, totalPages: 1 } };
    } catch (error) {
      // Only log parsing errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error parsing API response:', error, response);
      }
      return { patterns: [], pagination: { totalCount: 0, totalPages: 1 } };
    }
  };
  
  // Extract data from response using robust parser
  const { patterns, pagination } = parseApiResponse(patternsResponse);

  // Pattern type options
  const patternTypeOptions = [
    { label: 'Contains - pattern appears anywhere in tag', value: 'CONTAINS' },
    { label: 'Exact Match - tag must exactly match pattern', value: 'EXACT' },
    { label: 'Starts With - tag begins with pattern', value: 'STARTS_WITH' },
    { label: 'Ends With - tag ends with pattern', value: 'ENDS_WITH' },
    { label: 'Word Boundary - pattern as complete word', value: 'WORD_BOUNDARY' },
    { label: 'Regular Expression - advanced pattern matching', value: 'REGEX' },
  ];

  // Pattern category options
  const categoryOptions = [
    { label: 'Primary', value: 'primary' },
    { label: 'Alias', value: 'alias' },
    { label: 'Legacy', value: 'legacy' },
  ];

  // Handlers
  const handleAddPattern = useCallback(() => {
    setEditingPattern(null);
    setFormData({
      marketplaceName: '',
      displayName: '',
      patternType: 'CONTAINS',
      patternValue: '',
      priority: 1,
      isActive: true,
      isCaseSensitive: false,
      category: 'primary',
      description: '',
    });
    setShowPatternModal(true);
  }, []);

  const handleEditPattern = useCallback((pattern) => {
    setEditingPattern(pattern);
    setFormData({
      marketplaceName: pattern.marketplaceName || '',
      displayName: pattern.displayName || '',
      patternType: pattern.patternType || 'CONTAINS',
      patternValue: pattern.patternValue || '',
      priority: pattern.priority || 1,
      isActive: pattern.isActive !== false,
      isCaseSensitive: pattern.isCaseSensitive || false,
      category: pattern.category || 'primary',
      description: pattern.description || '',
    });
    setShowPatternModal(true);
  }, []);

  const handleSavePattern = useCallback(async () => {
    try {
      // Validate required fields
      if (!formData.marketplaceName?.trim()) {
        throw new Error('Marketplace name is required');
      }
      if (!formData.patternValue?.trim()) {
        throw new Error('Pattern value is required');
      }
      
      // Validate REGEX patterns
      if (formData.patternType === 'REGEX') {
        try {
          new RegExp(formData.patternValue);
        } catch (regexError) {
          throw new Error('Invalid regular expression pattern');
        }
      }
      
      // Sanitize and validate form data
      const sanitizedData = {
        ...formData,
        marketplaceName: formData.marketplaceName.trim(),
        displayName: formData.displayName?.trim() || '',
        patternValue: formData.patternValue.trim(),
        priority: Math.max(1, Math.min(10, formData.priority || 1)),
        description: formData.description?.trim() || ''
      };
      
      if (editingPattern) {
        await updatePattern.mutateAsync({
          id: editingPattern.id,
          ...sanitizedData,
        });
      } else {
        await createPattern.mutateAsync(sanitizedData);
      }
      setShowPatternModal(false);
    } catch (error) {
      // Only log detailed errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error saving pattern:', error);
      }
      // Show user-friendly error message
      alert(error.message || 'Failed to save pattern. Please check your input and try again.');
    }
  }, [editingPattern, formData, createPattern, updatePattern]);

  const handleDeletePattern = useCallback(async (patternId) => {
    if (window.confirm('Are you sure you want to delete this marketplace pattern?')) {
      try {
        await deletePattern.mutateAsync(patternId);
      } catch (error) {
        // Only log detailed errors in development
        if (process.env.NODE_ENV === 'development') {
          console.error('MarketplacePatterns: Error deleting pattern:', error);
        }
      }
    }
  }, [deletePattern]);

  // Helper functions
  const getPatternTypeTone = (type) => {
    switch (type) {
      case 'EXACT': return 'success';
      case 'CONTAINS': return 'info';
      case 'REGEX': return 'warning';
      default: return 'subdued';
    }
  };

  const getPatternHelpText = (type) => {
    switch (type) {
      case 'EXACT': return 'Tag must match exactly (e.g., "target")';
      case 'CONTAINS': return 'Tag must contain pattern (e.g., "target" matches "target-order-123")';
      case 'STARTS_WITH': return 'Tag must start with pattern (e.g., "target-")';
      case 'ENDS_WITH': return 'Tag must end with pattern (e.g., "-target")';
      case 'WORD_BOUNDARY': return 'Pattern as complete word (e.g., "target" but not "targetplus")';
      case 'REGEX': return 'Regular expression pattern (use carefully)';
      default: return '';
    }
  };

  // Safe table data rendering with error handling
  const tableRows = patterns.map((pattern) => {
    try {
      if (!pattern || typeof pattern !== 'object') {
        return null;
      }
      
      return [
        pattern.marketplaceName || 'Unknown',
        pattern.patternValue || '',
        <Badge tone={getPatternTypeTone(pattern.patternType || 'CONTAINS')}>{pattern.patternType || 'CONTAINS'}</Badge>,
        pattern.category || 'primary',
        pattern.priority || 1,
        <Badge tone={pattern.isActive !== false ? 'success' : 'critical'}>
          {pattern.isActive !== false ? 'Active' : 'Inactive'}
        </Badge>,
        <ButtonGroup variant="segmented">
          <Tooltip content="Edit Pattern">
            <Button
              size="micro"
              variant="tertiary"
              icon={EditIcon}
              onClick={() => handleEditPattern(pattern)}
              disabled={!pattern.id}
            />
          </Tooltip>
          <Tooltip content="Delete Pattern">
            <Button
              size="micro"
              variant="tertiary"
              tone="critical"
              icon={DeleteIcon}
              onClick={() => handleDeletePattern(pattern.id)}
              disabled={!pattern.id}
            />
          </Tooltip>
        </ButtonGroup>,
      ];
    } catch (error) {
      // Only log rendering errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('MarketplacePatterns: Error rendering pattern row:', error, pattern);
      }
      return null;
    }
  }).filter(Boolean); // Remove any null entries

  // Loading state
  if (isLoading && patterns.length === 0) {
    return (
      <Page title="Marketplace Pattern Management" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">
                      Loading marketplace patterns...
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Error state
  if (error) {
    return (
      <Page title="Marketplace Pattern Management" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load marketplace patterns. {error.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Empty state
  const emptyStateMarkup = (
    <EmptyState
      heading="No marketplace patterns found"
      content="Create your first marketplace pattern to start detecting marketplaces in order tags."
      image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
      action={{
        content: 'Add Pattern',
        onAction: handleAddPattern,
      }}
    />
  );

  return (
    <Page
      title="Marketplace Pattern Management"
      fullWidth
      subtitle={`${patterns.length} patterns configured`}
      backAction={{ content: 'Dashboard', url: '/' }}
      primaryAction={{
        content: 'Add Pattern',
        icon: PlusIcon,
        onAction: handleAddPattern,
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: () => refetch(),
          loading: isLoading,
        },
      ]}
    >
      <Layout>
        {/* Filter Bar */}
        <Layout.Section>
          <Card>
            <Box padding="300">
              <InlineStack gap="300" align="start" blockAlign="end">
                <Box width="300px">
                  <TextField
                    label="Search"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search marketplace or pattern..."
                    prefix={<SearchIcon />}
                    clearButton
                    onClearButtonClick={() => setSearchQuery('')}
                    autoComplete="off"
                  />
                </Box>
                
                <Box width="150px">
                  <Select
                    label="Status"
                    options={[
                      { label: 'All', value: '' },
                      { label: 'Active', value: 'true' },
                      { label: 'Inactive', value: 'false' },
                    ]}
                    value={statusFilter}
                    onChange={setStatusFilter}
                  />
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Main Patterns Table */}
        <Layout.Section>
          <Card>
            {patterns.length === 0 ? (
              emptyStateMarkup
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
                headings={[
                  'Marketplace',
                  'Pattern Value',
                  'Pattern Type',
                  'Category',
                  'Priority',
                  'Status',
                  'Actions',
                ]}
                rows={tableRows}
                loading={isLoading && isPreviousData}
              />
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box padding="400" borderBlockStart="border">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="bodySm" tone="subdued">
                    Showing {Math.max(1, ((currentPage - 1) * pageSize) + 1)}–{Math.min(currentPage * pageSize, pagination.totalCount || 0)} of {(pagination.totalCount || 0).toLocaleString()} patterns
                  </Text>
                  <InlineStack gap="200">
                    <Button
                      disabled={currentPage <= 1 || isLoading}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      size="micro"
                    >
                      Previous
                    </Button>
                    <Text variant="bodySm">
                      Page {currentPage} of {pagination.totalPages}
                    </Text>
                    <Button
                      disabled={currentPage >= pagination.totalPages || isLoading || isPreviousData}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      size="micro"
                    >
                      Next
                    </Button>
                  </InlineStack>
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Pattern Modal */}
      <Modal
        open={showPatternModal}
        onClose={() => setShowPatternModal(false)}
        title={editingPattern ? 'Edit Marketplace Pattern' : 'Add Marketplace Pattern'}
        primaryAction={{
          content: editingPattern ? 'Update Pattern' : 'Create Pattern',
          onAction: handleSavePattern,
          loading: createPattern.isLoading || updatePattern.isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowPatternModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text tone="subdued" variant="bodyMd">
              Create marketplace detection patterns to automatically identify which marketplace an order came from based on order tags.
            </Text>
            
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Marketplace Name"
                  value={formData.marketplaceName}
                  onChange={(value) => setFormData({ ...formData, marketplaceName: value })}
                  placeholder="e.g., Target, Amazon, Macy's"
                  helpText="The name of the marketplace (e.g., Target, Amazon, Macy's)"
                  requiredIndicator
                />
                <TextField
                  label="Display Name"
                  value={formData.displayName}
                  onChange={(value) => setFormData({ ...formData, displayName: value })}
                  placeholder="Leave blank to use marketplace name"
                  helpText="Optional custom display name. If left blank, marketplace name will be used."
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Select
                  label="Pattern Type"
                  options={patternTypeOptions}
                  value={formData.patternType}
                  onChange={(value) => setFormData({ ...formData, patternType: value })}
                  helpText="How the pattern should match order tags"
                  requiredIndicator
                />
                <TextField
                  label="Pattern Value"
                  value={formData.patternValue}
                  onChange={(value) => setFormData({ ...formData, patternValue: value })}
                  placeholder="e.g., target, amazon-fba, walmart.com"
                  helpText={getPatternHelpText(formData.patternType)}
                  requiredIndicator
                />
              </FormLayout.Group>

              {/* <FormLayout.Group>
                <TextField
                  label="Priority"
                  type="number"
                  value={formData.priority.toString()}
                  onChange={(value) => {
                    const numValue = parseInt(value, 10);
                    const safeValue = isNaN(numValue) || numValue < 1 || numValue > 10 ? 1 : numValue;
                    setFormData({ ...formData, priority: safeValue });
                  }}
                  placeholder="1-10"
                  helpText="Pattern matching priority (1-10). Higher numbers are checked first."
                  min={1}
                  max={10}
                />
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={formData.category}
                  onChange={(value) => setFormData({ ...formData, category: value })}
                  helpText="Pattern category for organization: Primary (main patterns), Alias (alternative names), Legacy (older patterns)"
                />
              </FormLayout.Group> */}

              <TextField
                label="Description"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                multiline={2}
                placeholder="e.g., Matches Target orders with standard tagging format"
                helpText="Internal notes about this pattern for other administrators"
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}